import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { EventAvailable, EventBusy } from '@material-ui/icons';
import { useBetween } from 'use-between';
import { DateTime } from 'luxon';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import {
  AvailabilityCenterDialog,
  EnrolmentDataType,
  ValueProps,
} from '../../../common/availability-center/AvailabilityCenterDialog';
import { useCampfireQuery } from '../../network/useCampfireQuery';
import { GET_ACTIVITYIDS } from '../../../common/models/program-activities.gql';
import { ProgramActivities, ProgramActivitiesVariables } from '../../../common/models/__generated__/ProgramActivities';
import { useUser } from '../../auth/useUser';
import { GET_ACTIVITIES } from './get-activities.gql';
import {
  AvailabilityCenterGetActivities,
  AvailabilityCenterGetActivitiesVariables,
} from './__generated__/AvailabilityCenterGetActivities';
import { GET_VOLUNTEER_UNAVAILABILITIES } from './get-volunteer-unavailabilities.gql';
import {
  GetVolunteerUnavailabilities,
  GetVolunteerUnavailabilitiesVariables,
} from './__generated__/GetVolunteerUnavailabilities';
import { GET_VOLUNTEER_ENROLMENTS_DATA } from '../../../common/models/volunteer-enrolments/volunteer-enrolments.gql';
import {
  GetVolunteerEnrolmentsData,
  GetVolunteerEnrolmentsDataVariables,
} from '../../../common/models/volunteer-enrolments/__generated__/GetVolunteerEnrolmentsData';
import { useCampfireFetch } from '../../network/useCampfireFetch';
import { useApiUrl } from '../../config/useApiUrl';
import { useSnackbar } from '../../config/useSnackbar';
import { useCampfireLazyQuery } from '../../network/useCampfireLazyQuery';
import { AlertFullSessionDialog } from '../../../common/alert-full-session-dialog/AlertFullSessionDialog';

const useAvailabilityCentreDialogState = () => {
  const [dialogOpen, setDialogOpen] = useState<boolean>();
  const [initialOperation, setInitialOperation] = useState<'availability' | 'unavailability' | undefined>();
  const [additionalRefetchFunctions, setAdditionalRefetchFunctions] = useState<Array<() => void>>([]);

  return {
    dialogOpen,
    setDialogOpen,
    initialOperation,
    setInitialOperation,
    additionalRefetchFunctions,
    setAdditionalRefetchFunctions,
  };
};

export const useSharedAvailabilityCentreDialogState = () => useBetween(useAvailabilityCentreDialogState);

export function AvailabilityCenter() {
  const {
    dialogOpen,
    setDialogOpen,
    initialOperation,
    setInitialOperation,
    additionalRefetchFunctions,
  } = useSharedAvailabilityCentreDialogState();
  const [value, setValue] = React.useState<ValueProps>({});
  const [fullSessionIds, setAllSessionIds] = React.useState<string[]>([]);
  const [activityDatesSubmited, setActivityDatesSubmited] = React.useState<string[]>([]);
  const [activityIdSubmited, setActivityIdSubmited] = React.useState<string>('');
  const [showAlertDialog, setShowAlertDialog] = React.useState(false);
  const { getVolunteerIdentity, user } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const { configItems, orgInfo } = user;

  const preventAvailability = configItems.vm.preventVolunteerAvailabilitySubmission === true;
  const allowUnavailability = configItems.vm.allowUnavailabilityAndLeave === true;

  // Full hide: prevent is ON and allow-unavailability is OFF
  const shouldHideAvailabilityCenter = preventAvailability && !allowUnavailability;

  // Unavailability-only mode: prevent is ON and allow-unavailability is ON
  const unavailabilityOnlyMode = preventAvailability && allowUnavailability;

  const [getEnrolmentData, { data: vmVolunteerData, refetch: refetchVolunteerData }] = useCampfireLazyQuery<
    GetVolunteerEnrolmentsData,
    GetVolunteerEnrolmentsDataVariables
  >(GET_VOLUNTEER_ENROLMENTS_DATA, {
    options: {
      variables: {
        volunteerId,
      },
    },
  });

  const [getUnavailabilities, { data: unavailabilitiesData }] = useCampfireLazyQuery<
    GetVolunteerUnavailabilities,
    GetVolunteerUnavailabilitiesVariables
  >(GET_VOLUNTEER_UNAVAILABILITIES, {
    options: {
      variables: {
        volunteerId,
      },
    },
  });

  const { data: vmPrograms } = useCampfireQuery<ProgramActivities, ProgramActivitiesVariables>(GET_ACTIVITYIDS, {
    options: {
      variables: {
        volunteerId,
      },
    },
  });

  const activityIds =
    vmPrograms?.vm.volunteer?.programs.flatMap((program) =>
      program.activities.map((activity) => activity.activityId)
    ) || [];

  const [getActivities, { data: vmActivities }] = useCampfireLazyQuery<
    AvailabilityCenterGetActivities,
    AvailabilityCenterGetActivitiesVariables
  >(GET_ACTIVITIES, {
    options: {
      variables: {
        activityIds,
        from: DateTime.local().toISODate(),
        until: DateTime.local()
          .plus({ years: 1 })
          .toISODate(),
      },
    },
  });

  React.useEffect(() => {
    if (dialogOpen) {
      getEnrolmentData({
        variables: {
          volunteerId,
        },
      });
      getUnavailabilities({
        variables: {
          volunteerId,
        },
      });
      getActivities({
        variables: {
          activityIds,
          from: DateTime.local().toISODate(),
          until: DateTime.local()
            .plus({ years: 1 })
            .toISODate(),
        },
      });
    }
  }, [dialogOpen]);

  // Set existing unavailabilities when data loads
  React.useEffect(() => {
    if (unavailabilitiesData?.vm?.volunteer?.unavailabilities) {
      const existingUnavailabilities = unavailabilitiesData.vm.volunteer.unavailabilities.map((u) => ({
        id: u.volunteerUnavailabilityId,
        startDate: u.startDate,
        endDate: u.endDate,
      }));
      setValue((prev) => ({
        ...prev,
        unavailabilities: existingUnavailabilities,
      }));
    }
  }, [unavailabilitiesData]);

  const onChange = (newValue: ValueProps) => {
    setValue(newValue);
  };

  const campfireMutation = useCampfireFetch({ defer: true });
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();

  const handleSubmit = (selectedValue: ValueProps) => {
    const { operation, activityId, sessionIds, dates, unavailabilities } = selectedValue;

    if (operation === 'unavailability') {
      // Handle bulk unavailability with new structure
      // Allow empty array to clear all unavailabilities
      if (unavailabilities === undefined) {
        return;
      }

      campfireMutation
        .run({
          url: `${apiUrl}/vm/activity/enrolment/unavailability/bulk-save`,
          method: 'post',
          data: {
            volunteerId: volunteerId,
            unavailabilities: unavailabilities,
          },
        })
        .then((response) => {
          if (!response.ok) {
            setSnackbar({
              variant: 'error',
              open: true,
              message: 'Failed to save bulk unavailability',
            });
            return;
          }

          setSnackbar({
            variant: 'success',
            open: true,
            message: 'Bulk unavailability saved successfully',
          });

          // Refetch data to update the UI
          if (refetchVolunteerData) {
            setTimeout(refetchVolunteerData, 0);
          }
          // Call any additional refetch functions passed from parent components
          additionalRefetchFunctions.forEach((refetchFn) => {
            setTimeout(refetchFn, 0);
          });
        })
        .catch(() => {
          setSnackbar({
            variant: 'error',
            open: true,
            message: 'Failed to save bulk unavailability',
          });
        });
    } else {
      // Handle regular availability
      if (!activityId || !dates?.length) {
        return;
      }
      const enrolment = vmVolunteerData?.vm.volunteer?.activityEnrolments.find(
        (activityEnrolment) => activityEnrolment.activity.activityId === activityId
      );
      campfireMutation
        .run({
          url: `${apiUrl}/vm/activity/enrolment/availability/save`,
          method: 'post',
          data: {
            activityId: activityId,
            activityEnrolmentId: enrolment?.activityEnrolmentId,
            activityDates: dates,
            activityRoleIds: [],
            sessionIds: sessionIds ?? [],
          },
        })
        .then((response) => {
          if (enrolment?.activity.hasOpenRoster) {
            campfireMutation.run({
              url: `${apiUrl}/vm/activity/enrolment/attendance/add`,
              method: 'post',
              data: {
                activityEnrolmentId: enrolment?.activityEnrolmentId,
                activityDates: dates,
                activityRoleIds: [],
                sessionIds: sessionIds ?? [],
                activityId: activityId,
                activityAvailabilityId: null,
                volunteerId: volunteerId,
              },
            });
          }
          const data = response.data.data as {
            activityAvailabilityId: string[];
            activityId: string;
            fullSessionIds: string[];
            activityDates: string[];
          };

          if (data && data.fullSessionIds?.length > 0 && data.activityId) {
            setAllSessionIds(data.fullSessionIds);
            setActivityIdSubmited(data.activityId);
            setActivityDatesSubmited(data.activityDates);
            setShowAlertDialog(true);
          }

          setSnackbar({
            variant: 'success',
            open: true,
            message: 'Update availability successfully',
          });

          // Refetch data to update the UI
          if (refetchVolunteerData) {
            setTimeout(refetchVolunteerData, 0);
          }
          // Call any additional refetch functions passed from parent components
          additionalRefetchFunctions.forEach((refetchFn) => {
            setTimeout(refetchFn, 0);
          });
        })
        .catch(() => {
          setSnackbar({
            variant: 'error',
            open: true,
            message: 'Failed to update availability',
          });
        });
    }
  };

  // Track if dialog just opened to avoid unnecessary resets
  const [dialogJustOpened, setDialogJustOpened] = React.useState(false);

  React.useEffect(() => {
    // Only initialize value when dialog first opens, not on every data change
    if (dialogOpen && !dialogJustOpened) {
      setDialogJustOpened(true);
      if (initialOperation === 'unavailability') {
        // For unavailability mode, set operation and load existing unavailabilities
        const existingUnavailabilities =
          unavailabilitiesData?.vm?.volunteer?.unavailabilities?.map((u) => ({
            id: u.volunteerUnavailabilityId,
            startDate: u.startDate,
            endDate: u.endDate,
          })) || [];
        setValue((prev) => ({
          ...prev,
          operation: 'unavailability',
          unavailabilities:
            existingUnavailabilities.length > 0 ? existingUnavailabilities : prev.unavailabilities || [],
        }));
      } else if (initialOperation) {
        setValue({ operation: initialOperation });
      }
      // Don't reset value when no initialOperation - let the data loading useEffect handle it
    } else if (!dialogOpen) {
      setDialogJustOpened(false);
    }
  }, [dialogOpen, initialOperation]);

  const { isMobile } = useCampfireTheme();

  const volunteerActivityRoleIds = React.useMemo(() => {
    return vmVolunteerData?.vm.volunteer?.activityRoles?.map((r: { activityRoleId: string }) => r.activityRoleId) ?? [];
  }, [vmVolunteerData]);

  const enrolmentDataByActivityId =
    vmVolunteerData?.vm.volunteer?.activityEnrolments.reduce(
      (acc, activityEnrolment) => ({
        ...acc,
        [activityEnrolment.activity.activityId]: {
          availabilities: activityEnrolment.availabilities
            .filter((availability) => !availability.dateRemoved)
            .map((availability) => availability.activityDate),
          unavailabilities: activityEnrolment.unavailabilities
            .filter((unavailability) => unavailability.dateRemoved)
            .map((unavailability) => unavailability.activityDate),
        },
      }),
      {} as EnrolmentDataType
    ) || {};

  const enrolmentActivities = vmActivities?.vm.activities.filter(
    (activity) => enrolmentDataByActivityId[activity.activityId as string]
  );

  const handleAlertClose = () => {
    setShowAlertDialog(false);
    setAllSessionIds([]);
    setActivityDatesSubmited([]);
    setActivityIdSubmited('');
  };

  // Don't render if config prevents volunteer availability submission
  if (shouldHideAvailabilityCenter) {
    return null;
  }

  return (
    <React.Fragment>
      <Box
        display={'flex'}
        borderRadius='6px'
        alignSelf='center'
        alignItems={'center'}
        border={'1px solid #dbdbdbb3'}
        padding={'6px 10px'}
        onClick={() => {
          if (unavailabilityOnlyMode) {
            setInitialOperation('unavailability');
          }
          setDialogOpen(true);
        }}
        style={{ cursor: 'pointer' }}
      >
        {unavailabilityOnlyMode ? (
          <EventBusy style={{ color: 'white', fontSize: '21px' }} />
        ) : (
          <EventAvailable style={{ color: 'white', fontSize: '21px' }} />
        )}
        {isMobile ? (
          ''
        ) : (
          <Typography style={{ paddingLeft: '6px', fontSize: '13px' }}>
            {unavailabilityOnlyMode ? 'Unavailability & Leave' : 'Availability Centre'}
          </Typography>
        )}
      </Box>
      {dialogOpen && (
        <AvailabilityCenterDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setInitialOperation(undefined);
          }}
          value={value}
          onChange={onChange}
          activities={enrolmentActivities || []}
          onSubmit={handleSubmit}
          enrolmentData={enrolmentDataByActivityId}
          initialOperation={initialOperation}
          availabilityRestrictionDays={orgInfo.availabilityRestrictionDays}
          unavailabilityOnly={unavailabilityOnlyMode}
          volunteerActivityRoleIds={volunteerActivityRoleIds}
        />
      )}
      {showAlertDialog && (
        <AlertFullSessionDialog
          open={showAlertDialog}
          onClose={handleAlertClose}
          fullSessionIds={fullSessionIds}
          activityIdSubmited={activityIdSubmited}
          activityDatesSubmited={activityDatesSubmited}
        />
      )}
    </React.Fragment>
  );
}
